import React from 'react'
import { Grid } from '@mui/material'
import Fade from '@mui/material/Fade'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import { useWallets } from 'providers/WalletsProvider'
import { getMoonpaySignature } from 'services/api.service'
import { Add } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import { createTheme, ThemeProvider } from '@mui/material/styles'
// Matches Banxa's DEFAULT_TOPUP_AMOUNT so both providers offer the same
// starting amount. The customer can still change it inside the widget.
const MOONPAY_TOPUP_AMOUNT = 100

interface MoonpayModalProps {
  open: boolean
  onClose: () => void
  mode: 'nft' | 'funds'
  packId?: string
}

const MoonpayModal = ({ open, onClose, packId, mode }: MoonpayModalProps) => {
  const {
    flowUser,
    crossmintWalletAddress,
    connectedWalletAddress,
    refreshCrossmintBalance
  } = useWallets()
  const [fullURL, setFullURL] = React.useState('')
  const [openDialog, setOpenDialog] = React.useState(false)
  function closeModal() {
    refreshCrossmintBalance()
    onClose()
    setOpenDialog(false)
  }

  const handleClickOpen = () => {
    setOpenDialog(true)
  }

  const handleClose = () => {
    setOpenDialog(false)
  }

  // Top-ups now target the Crossmint wallet on Base. Previously this whole
  // effect was gated on `flowUser?.addr` and passed that Flow address as
  // walletAddress — a Crossmint user has no flowUser, so the effect returned
  // early, fullURL stayed '' and the iframe rendered blank with no error.
  const destinationAddress = crossmintWalletAddress || connectedWalletAddress

  React.useEffect(() => {
    if (!destinationAddress) return
    if (mode === 'nft' && !packId) return

    // NFT checkout is the Flow-era path: VITE_AFL_CONTRACTS is a Flow contract
    // id, not an EVM address. Packs are bought with USDC directly now, so this
    // branch is unreachable in practice and left as-is rather than half-ported.
    const nftURL = `${import.meta.env.VITE_MOONPAY_BASE_URL}/nft?apiKey=${
      import.meta.env.VITE_MOONPAY_KEY
    }&contractAddress=${
      import.meta.env.VITE_AFL_CONTRACTS
    }&tokenId=${packId}&currencyCode=usdc&theme=dark&baseCurrencyCode=aud&walletAddress=${
      flowUser?.addr
    }`

    // `usdc` is Ethereum USDC (chainId 1). Base USDC is a distinct MoonPay
    // currency, `usdc_base` (chainId 8453,
    // 0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913), which matches the token the
    // backend and packs use. Sending `usdc` would have delivered funds on
    // Ethereum, out of reach of the Base wallet they were bought for.
    const fundsURL = `${import.meta.env.VITE_MOONPAY_BASE_URL}?apiKey=${
      import.meta.env.VITE_MOONPAY_KEY
    }&currencyCode=usdc_base&theme=dark&baseCurrencyCode=aud&baseCurrencyAmount=${MOONPAY_TOPUP_AMOUNT}&walletAddress=${destinationAddress}`

    const url = mode === 'nft' ? nftURL : fundsURL
    const getFullURL = async () => {
      const fURL = await getMoonpaySignature(url)
      setFullURL(fURL)
    }
    getFullURL()
  }, [destinationAddress, flowUser, packId, mode])

  const theme = createTheme({
    palette: {
      mode: 'dark'
    }
  })

  return (
    <ThemeProvider theme={theme}>
      <Modal
        open={open}
        onClose={handleClickOpen}
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <Grid
            container
            justifyContent="center"
            xs={11}
            sm={8}
            md={6}
            lg={4}
            xl={3}
            item
            className="absolute right-0 min-h-full w-[340px] !max-w-[340px] overflow-visible bg-[#1c1c1e] text-center text-black"
          >
            <Grid item xs={11} className="pt-8 md:pt-10">
              <div
                onClick={handleClickOpen}
                className="absolute left-[-30px] top-0 flex h-[30px] w-[30px] cursor-pointer items-center justify-center bg-white"
              >
                <Add
                  fontSize="large"
                  style={{
                    transform: `rotate(45deg)`,
                    color: '#000000'
                  }}
                />
              </div>
              <iframe
                allow="accelerometer; autoplay; camera; gyroscope; payment"
                frameBorder="0"
                height="95%"
                src={fullURL}
                width="100%"
              >
                <p>Your browser does not support iframes.</p>
              </iframe>
            </Grid>
          </Grid>
        </Fade>
      </Modal>
      <Dialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Exit the purchase'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to exit the process?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Cancel
          </Button>
          <Button onClick={closeModal}>Exit</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default MoonpayModal
